import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@pushpress/pushpress-ui";
import ChatPromptInput from "./ChatPromptInput";
import { useEffect, useRef, useState, useMemo } from "react";
import { AIMessage } from "../types";
import {
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  SearchIcon,
  SquarePenIcon,
  EllipsisVerticalIcon,
  TrashIcon,
  PencilIcon,
} from "lucide-react";
import {
  useGetChats,
  useGetHistoricalChat,
  useRenameChat,
  useDeleteChat,
} from "../../../apis/aiApi.hooks";
import { useTypewriterEffect } from "./hooks";
import { AI_ROLES } from "../constants";
import Interruption from "./Interruption";
import PPIcon from "../../../assets/img/pp-icon.svg";
import { GetChatsItem } from "src/apis/aiApi";

type ChatModalProps = {
  onClose: () => void;
  onSend: (
    message: string,
    interruptionStatus?: "approved" | "rejected",
  ) => void;
  messages: AIMessage[];
  setMessages: (messages: AIMessage[]) => void;
  setChatUuid: (chatUuid: string) => void;
  isLoading: boolean;
  streamedMessage: string;
};

export default function ChatModal({
  onClose,
  onSend,
  messages,
  setMessages,
  setChatUuid,
  isLoading,
  streamedMessage,
}: ChatModalProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [selectedChatUuid, setSelectedChatUuid] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [dropdownOpenChatUuid, setDropdownOpenChatUuid] = useState<
    string | null
  >(null);
  const [chatList, setChatList] = useState<GetChatsItem[]>();
  const [renamingChatUuid, setRenamingChatUuid] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [shouldMoveSelectedToTop, setShouldMoveSelectedToTop] = useState(false);
  const { data: chatsData, refetch: refetchChats } = useGetChats();
  const renameChatMutation = useRenameChat();
  const deleteChatMutation = useDeleteChat();
  const { data: historicalChatData } = useGetHistoricalChat(
    selectedChatUuid || "",
    {
      skip: !selectedChatUuid,
    },
  );
  const [selectedChatTitle, setSelectedChatTitle] = useState<string | null>(
    historicalChatData?.title || null,
  );

  const { displayedText: displayedStreamedMessage, isTypingComplete } =
    useTypewriterEffect(streamedMessage, 1);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages, displayedStreamedMessage]);

  useEffect(() => {
    setChatList(chatsData?.chats);
  }, [chatsData]);

  useEffect(() => {
    if (historicalChatData && selectedChatUuid) {
      setMessages(historicalChatData.chatHistory);
      setSelectedChatTitle(historicalChatData.title);
    }
  }, [historicalChatData, selectedChatUuid, setMessages]);

  useEffect(() => {
    setChatList(
      chatsData?.chats.filter((chat) =>
        chat.title?.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    );
  }, [searchQuery, chatsData?.chats]);

  // Move selected chat to the top of the list locally
  const sortedChatList = useMemo(() => {
    if (!chatList) return [];

    const sorted = [...chatList];
    if (selectedChatUuid && shouldMoveSelectedToTop) {
      const selectedChatIndex = sorted.findIndex(
        (chat) => chat.chatUuid === selectedChatUuid,
      );
      if (selectedChatIndex > -1) {
        const selectedChat = sorted.splice(selectedChatIndex, 1)[0];
        sorted.unshift(selectedChat);
      }
    }
    return sorted;
  }, [chatList, selectedChatUuid, shouldMoveSelectedToTop]);

  const handleSend = async (
    message: string,
    interruptionStatus?: "approved" | "rejected",
  ) => {
    setShouldMoveSelectedToTop(true); // Move the selected chat to the top of the list locally as soon as the user sends a message
    await onSend(message, interruptionStatus); // Send the message to the AI
    await refetchChats(); // Refresh the chat list after sending a message
  };

  const handleRename = async (chatUuid: string, newTitle: string) => {
    try {
      // Call the API to rename the chat
      await renameChatMutation.mutateAsync({ chatUuid, newTitle });

      // Update the chat title in the local state
      setChatList((prevChats) =>
        prevChats?.map((chat) =>
          chat.chatUuid === chatUuid ? { ...chat, title: newTitle } : chat,
        ),
      );

      setRenamingChatUuid(null);
      setRenameValue("");

      // Handle the case where the chat is selected and the title is being renamed
      if (selectedChatUuid === chatUuid) {
        setSelectedChatTitle(newTitle);
      }
    } catch (error) {
      console.error("Error renaming chat:", error);
    }
  };

  const startRename = (chatUuid: string, currentTitle: string) => {
    setRenamingChatUuid(chatUuid);
    setRenameValue(currentTitle);
  };

  const cancelRename = () => {
    setRenamingChatUuid(null);
    setRenameValue("");
  };

  const handleDelete = async (chatUuid: string) => {
    try {
      await deleteChatMutation.mutateAsync({ chatUuid });
      setChatList((prevChats) =>
        prevChats?.filter((chat) => chat.chatUuid !== chatUuid),
      );
    } catch (error) {
      console.error("Error deleting chat:", error);
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <div>
          <DialogContent className="tw-px-0 tw-py-0 tw-flex tw-flex-col !tw-m-0 !tw-w-[100vw] !tw-h-[100vh] !tw-max-w-[100vw] !tw-rounded-none">
            <div className="tw-flex tw-flex-1 tw-flex-grow-1 tw-min-h-0 tw-overflow-hidden">
              <div
                className={`tw-bg-gray-50 tw-h-full tw-mt-0 tw-transition-all tw-duration-300 tw-relative ${isNavCollapsed ? "tw-w-0 tw-opacity-0 tw-p-0 tw-pointer-events-none" : "tw-w-[300px]"}`}
              >
                <div className="tw-flex tw-items-center tw-justify-start tw-gap-2 tw-mb-4 tw-border-b tw-border-neutral-200 tw-shadow-md tw-p-4">
                  <h2 className="tw-text-lg tw-font-normal">Assistant</h2>
                  <PanelLeftCloseIcon
                    size={24}
                    className="tw-absolute tw-right-2 tw-top-4 tw-p-1 tw-text-neutral-600"
                    onClick={() => setIsNavCollapsed(true)}
                  />
                </div>
                <div className="tw-px-4 tw-flex tw-flex-col tw-gap-2 tw-h-full">
                  <Button
                    variant="ghost"
                    className="tw-w-full tw-text-left tw-justify-start hover:tw-bg-neutral-200 tw-px-4 tw-flex-shrink-0"
                    innerClassName="!tw-justify-start"
                    onClick={() => {
                      setSelectedChatUuid(null);
                      setChatUuid(null);
                      setMessages([]);
                      setSelectedChatTitle(null);
                      setShouldMoveSelectedToTop(false);
                    }}
                  >
                    <div className="tw-flex tw-items-center tw-gap-4 tw-text-neutral-800 tw-justify-start">
                      <SquarePenIcon
                        size={16}
                        className="tw-text-neutral-800"
                      />
                      <span>New chat</span>
                    </div>
                  </Button>
                  <div className="tw-flex tw-gap-2 tw-items-center tw-px-4 tw-flex-shrink-0">
                    <SearchIcon size={16} className="tw-text-neutral-500" />
                    <Input
                      placeholder="Search your chats"
                      className="tw-w-full tw-py-4 tw-pr-4 tw-pl-2 tw-text-base !tw-border-none !tw-outline-none !tw-shadow-none focus:!tw-border-none focus:!tw-outline-none focus:!tw-shadow-none focus:!tw-ring-0 focus-visible:!tw-ring-0 focus-visible:!tw-outline-none"
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                      }}
                    />
                  </div>
                  <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-pt-4">
                    <h3 className="tw-text-sm tw-text-neutral-500 tw-px-4 tw-mb-2">
                      Chats
                    </h3>
                    <div className="tw-space-y-1">
                      {sortedChatList?.map((chat: GetChatsItem) => (
                        <div
                          key={chat.chatUuid}
                          className={`tw-w-full tw-flex tw-items-center tw-px-1 tw-py-1 hover:tw-bg-neutral-200 tw-rounded group ${
                            selectedChatUuid === chat.chatUuid ||
                            dropdownOpenChatUuid === chat.chatUuid
                              ? "tw-bg-neutral-200"
                              : ""
                          }`}
                          onMouseEnter={(e) => {
                            const button = e.currentTarget.querySelector(
                              "[data-ellipsis]",
                            ) as HTMLElement;
                            if (
                              button &&
                              dropdownOpenChatUuid !== chat.chatUuid
                            ) {
                              button.classList.remove("tw-invisible");
                            }
                          }}
                          onMouseLeave={(e) => {
                            const button = e.currentTarget.querySelector(
                              "[data-ellipsis]",
                            ) as HTMLElement;
                            if (
                              button &&
                              dropdownOpenChatUuid !== chat.chatUuid
                            ) {
                              button.classList.add("tw-invisible");
                            }
                          }}
                        >
                          {renamingChatUuid === chat.chatUuid ? (
                            // Inline edit mode
                            <div className="tw-flex tw-items-center tw-gap-2 tw-w-full">
                              <Input
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                className="tw-flex-1 tw-h-8 tw-text-sm"
                                onKeyDown={async (e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    await handleRename(
                                      chat.chatUuid,
                                      renameValue,
                                    );
                                  } else if (e.key === "Escape") {
                                    cancelRename();
                                  }
                                }}
                                onBlur={async () => {
                                  if (renameValue.trim()) {
                                    await handleRename(
                                      chat.chatUuid,
                                      renameValue,
                                    );
                                  } else {
                                    cancelRename();
                                  }
                                }}
                              />
                            </div>
                          ) : (
                            // Normal display mode
                            <Button
                              variant="ghost"
                              className="tw-w-full tw-text-left tw-justify-start hover:tw-bg-neutral-200 tw-p-0 tw-h-auto"
                              innerClassName="tw-text-neutral-800"
                              onClick={() => {
                                setChatUuid(chat.chatUuid);
                                setSelectedChatUuid(chat.chatUuid);
                                setShouldMoveSelectedToTop(false);
                              }}
                            >
                              <span className="tw-truncate tw-block tw-w-full">
                                {chat.title}
                              </span>
                            </Button>
                          )}

                          <DropdownMenu
                            onOpenChange={(open) => {
                              setDropdownOpenChatUuid(
                                open ? chat.chatUuid : null,
                              );
                              // Keep ellipsis visible when dropdown is open
                              const button = document.querySelector(
                                `[data-ellipsis]`,
                              ) as HTMLElement;
                              if (button) {
                                if (open) {
                                  button.classList.remove("tw-invisible");
                                } else {
                                  // Only hide if mouse is not over the row
                                  const row = button.closest(".group");
                                  if (row && !row.matches(":hover")) {
                                    button.classList.add("tw-invisible");
                                  }
                                }
                              }
                            }}
                          >
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="tw-h-6 tw-w-6 tw-p-0 tw-text-neutral-600 hover:tw-text-neutral-800 tw-flex tw-items-center tw-justify-center tw-min-w-0 tw-invisible tw-transition-all"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                data-ellipsis="true"
                              >
                                <EllipsisVerticalIcon
                                  size={16}
                                  className="tw-text-current tw-flex-shrink-0"
                                />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              className="!tw-w-24 !tw-min-w-24"
                              align="end"
                            >
                              <DropdownMenuItem
                                className="tw-flex tw-items-center tw-gap-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startRename(chat.chatUuid, chat.title || "");
                                }}
                              >
                                <PencilIcon
                                  size={16}
                                  className="tw-text-neutral-800"
                                />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="tw-flex tw-items-center tw-gap-2 tw-text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(chat.chatUuid);
                                }}
                              >
                                <TrashIcon
                                  size={16}
                                  className="tw-text-red-600"
                                />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="tw-flex-1 tw-bg-white tw-h-full tw-flex tw-flex-col tw-mt-0 tw-min-w-0 tw-min-h-0">
                <DialogHeader className="tw-flex tw-items-center tw-justify-start tw-gap-2 tw-mb-4 tw-border-b tw-border-neutral-200 tw-shadow-md tw-p-4">
                  <DialogTitle className="!tw-text-left !tw-flex !tw-items-center !tw-justify-start !tw-gap-2 !tw-leading-[1.75rem] !tw-w-full">
                    {isNavCollapsed && (
                      <PanelLeftOpenIcon
                        onClick={() => setIsNavCollapsed(false)}
                        className="tw-w-4 tw-h-4"
                      />
                    )}
                    {selectedChatTitle || "New chat"}
                  </DialogTitle>
                </DialogHeader>
                {/* Right side fills remaining space */}
                <div
                  ref={messagesEndRef}
                  className="tw-flex-1 tw-overflow-y-auto tw-bg-white tw-h-full tw-flex tw-flex-col tw-p-4"
                >
                  {messages.map(
                    (message, index) =>
                      (isTypingComplete ||
                        message.role === AI_ROLES.USER ||
                        (!isTypingComplete &&
                          index !== messages.length - 1)) && (
                        <div key={index}>
                          {/* If this is the last message in the list of messages and is an interruption, we want to show the interruption UI. */}
                          {message.interruptionName ? (
                            <>
                              <div className="tw-flex tw-items-center tw-gap-1">
                                <PPIcon width={32} height={32} />
                                <span className="tw-text-md tw-text-neutral-400">
                                  Assistant
                                </span>
                              </div>
                              <Interruption
                                interruptionName={message.interruptionName}
                                data={message.data}
                                confirm={
                                  index === messages.length - 1 &&
                                  !message.interruptionStatus &&
                                  !isLoading &&
                                  isTypingComplete
                                    ? () => {
                                        handleSend(message.content, "approved");
                                      }
                                    : undefined
                                }
                                cancel={
                                  index === messages.length - 1 &&
                                  !message.interruptionStatus &&
                                  !isLoading &&
                                  isTypingComplete
                                    ? () => {
                                        handleSend(message.content, "rejected");
                                      }
                                    : undefined
                                }
                              />
                            </>
                          ) : (
                            // If this is the last message in the list of messages and output streaming is not actively happening, we want to show the message.
                            <div className="tw-flex tw-flex-col">
                              {message.role === AI_ROLES.ASSISTANT && (
                                <div className="tw-flex tw-items-center tw-gap-1">
                                  <PPIcon width={32} height={32} />
                                  <span className="tw-text-md tw-text-neutral-400">
                                    Assistant
                                  </span>
                                </div>
                              )}
                              <div
                                className={`tw-p-4 tw-max-w-full tw-break-words tw-w-3/5 ${
                                  message.role === AI_ROLES.USER
                                    ? "tw-bg-neutral-200 tw-text-neutral-800 tw-self-end tw-w-auto tw-max-w-3/4 tw-m-2 tw-rounded-full tw-px-6"
                                    : "tw-self-start tw-text-neutral-800"
                                }`}
                              >
                                <p className="tw-m-0 tw-whitespace-pre-wrap">
                                  {message.content}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ),
                  )}
                  {/* Display the streamed message using the typewriter effect */}
                  {!isTypingComplete && (
                    <>
                      <div className="tw-flex tw-items-center tw-gap-1">
                        <PPIcon width={32} height={32} />
                        <span className="tw-text-md tw-text-neutral-400">
                          Assistant
                        </span>
                      </div>
                      <div className="tw-p-4 tw-max-w-full tw-break-words tw-w-3/5 tw-self-start tw-text-neutral-800">
                        <p className="tw-m-0 tw-whitespace-pre-wrap">
                          {displayedStreamedMessage}
                          {/* This will animate character by character */}
                        </p>
                      </div>
                    </>
                  )}
                  {/* "Thinking..." indicator only shows if chat endpoint is called and we are waiting to start streaming */}
                  {isLoading && isTypingComplete && messages.length > 0 && (
                    <div className="tw-flex tw-items-center tw-gap-1">
                      <PPIcon
                        width={32}
                        height={32}
                        className="tw-text-primary"
                      />
                      <span className="tw-text-md tw-text-neutral-400">
                        Working...
                      </span>
                    </div>
                  )}
                </div>
                <div className="tw-px-4 tw-pt-2">
                  <ChatPromptInput onSend={onSend} />
                </div>
              </div>
            </div>
          </DialogContent>
        </div>
      </Dialog>
    </>
  );
}
